// Supabase REST bridge for the creator app.
//
// Tiny fetch wrappers over Supabase PostgREST — no @supabase/supabase-js
// dependency (the npm package is ~80KB and ships a realtime client we
// don't need yet). Calls the project's REST endpoint with the anon key
// from the public runtime config injected at HTML serve time.
//
// All writes are FIRE-AND-FORGET. localStorage stays the source of
// truth in apps/creator/src/lib/recent-apps.ts; Supabase is the dual-
// write tier that lets a signed-in user see their workspace on a
// different device. A failed Supabase call never blocks the UI.

interface PomelaRuntimeConfig {
  supabaseUrl?: string;
  supabaseAnonKey?: string;
}

declare global {
  interface Window {
    __POMELA_RUNTIME_CONFIG__?: PomelaRuntimeConfig;
  }
}

function cfg(): PomelaRuntimeConfig {
  // The creator UI reads from Vite's import.meta.env (build-time inlined
  // VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY from .env.local). The
  // generated app's runtime uses window.__POMELA_RUNTIME_CONFIG__ which
  // the worker injects into the HTML. Either surface wins — whichever
  // is populated.
  const fromEnv: PomelaRuntimeConfig = {
    supabaseUrl: (import.meta.env.VITE_SUPABASE_URL as string | undefined) || undefined,
    supabaseAnonKey: (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) || undefined
  };
  const fromWindow: PomelaRuntimeConfig =
    typeof window !== 'undefined' && window.__POMELA_RUNTIME_CONFIG__ ? window.__POMELA_RUNTIME_CONFIG__ : {};
  return {
    supabaseUrl: fromWindow.supabaseUrl ?? fromEnv.supabaseUrl,
    supabaseAnonKey: fromWindow.supabaseAnonKey ?? fromEnv.supabaseAnonKey
  };
}

/**
 * Whether the runtime is wired with Supabase keys. UI surfaces (e.g.
 * "Signed in" banner on Workspace) can branch on this.
 */
export function supabaseAvailable(): boolean {
  const c = cfg();
  return !!(c.supabaseUrl && c.supabaseAnonKey);
}

/**
 * Tag a fetch with the Supabase auth headers. Pass the creator's Supabase-RLS
 * JWT (from getCreatorToken) so the `apps_owner` RLS policy (account_id =
 * jwt.sub) authorises the write; without it the anon role is used and RLS
 * blocks owner-scoped writes (42501) — by design.
 */
function authHeaders(jwt?: string | null): Record<string, string> {
  const c = cfg();
  if (!c.supabaseUrl || !c.supabaseAnonKey) return {};
  return {
    apikey: c.supabaseAnonKey,
    authorization: `Bearer ${jwt ?? c.supabaseAnonKey}`,
    accept: 'application/json',
    'content-type': 'application/json'
  };
}

// §8.2 — the creator's Supabase-RLS JWT. Minted server-side from the HttpOnly
// session by POST /v1/auth/creator-token (the browser can't forge `sub`).
// Cached in-memory for its lifetime; refetched when near expiry.
let creatorTokenCache: { token: string; accountId: string; expiresAt: number } | null = null;

export async function getCreatorToken(): Promise<{ token: string; accountId: string } | null> {
  const now = Date.now();
  if (creatorTokenCache && creatorTokenCache.expiresAt - now > 60_000) {
    return { token: creatorTokenCache.token, accountId: creatorTokenCache.accountId };
  }
  try {
    const res = await fetch('/v1/auth/creator-token', { method: 'POST', credentials: 'include' });
    if (!res.ok) return null;
    const data = (await res.json()) as { token?: string; accountId?: string; expiresIn?: number };
    if (!data.token || !data.accountId) return null;
    creatorTokenCache = { token: data.token, accountId: data.accountId, expiresAt: now + (data.expiresIn ?? 3600) * 1000 };
    return { token: data.token, accountId: data.accountId };
  } catch {
    return null;
  }
}

/** A creator's app row, shaped for the Supabase `apps` schema (migration 002). */
export interface AppRow {
  appId: string;
  name: string;
  prompt: string;
  primaryColor?: string | null;
  previewUrl?: string | null;
  ogImageUrl?: string | null;
}

/**
 * Persist a creator's app to Supabase as a fire-and-forget dual-write alongside
 * saveRecentApp(). Supabase is the cloud source of truth; localStorage is the
 * cache. Uses the creator's RLS JWT so the row lands under their account and is
 * isolated from other creators. The `apps` schema requires account_id/slug/
 * prompt/spec/blueprint (all NOT NULL); the full AppSpec lives in the
 * gen-worker's store, so `spec` here holds a lightweight metadata pointer.
 */
export async function upsertApp(app: AppRow): Promise<boolean> {
  const c = cfg();
  if (!c.supabaseUrl || !c.supabaseAnonKey) return false;
  const auth = await getCreatorToken();
  if (!auth) return false; // not signed in → cloud persistence is a no-op
  // Only real `apps` columns (migration 002). There are no preview_url/
  // og_image_url columns — the URLs live in the `spec` jsonb pointer.
  const row = {
    account_id: auth.accountId,
    slug: app.appId, // appId is already unique + url-safe → use as the unique slug
    title: app.name,
    prompt: app.prompt,
    blueprint: 'spec',
    primary_color: app.primaryColor ?? null,
    spec: { appId: app.appId, previewUrl: app.previewUrl ?? null, ogImageUrl: app.ogImageUrl ?? null, source: 'creator-dual-write' }
  };
  try {
    const res = await fetch(`${c.supabaseUrl}/rest/v1/apps?on_conflict=slug`, {
      method: 'POST',
      headers: { ...authHeaders(auth.token), Prefer: 'resolution=merge-duplicates,return=minimal' },
      body: JSON.stringify(row),
      keepalive: true
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Hydrate the creator's app list from Supabase (cloud → cache restore after
 *  localStorage eviction / on a new device). RLS scopes it to their own rows. */
export async function listCreatorApps(): Promise<AppRow[]> {
  const c = cfg();
  if (!c.supabaseUrl || !c.supabaseAnonKey) return [];
  const auth = await getCreatorToken();
  if (!auth) return [];
  try {
    const res = await fetch(
      `${c.supabaseUrl}/rest/v1/apps?account_id=eq.${encodeURIComponent(auth.accountId)}&deleted_at=is.null&order=updated_at.desc&select=slug,title,prompt,primary_color,spec`,
      { headers: authHeaders(auth.token) }
    );
    if (!res.ok) return [];
    const rows = (await res.json()) as Array<Record<string, unknown>>;
    return rows.map((r) => {
      const spec = (r.spec ?? {}) as { previewUrl?: string | null; ogImageUrl?: string | null };
      return {
        appId: String(r.slug ?? ''),
        name: String(r.title ?? 'Untitled'),
        prompt: String(r.prompt ?? ''),
        primaryColor: (r.primary_color as string | null) ?? null,
        previewUrl: spec.previewUrl ?? null,
        ogImageUrl: spec.ogImageUrl ?? null
      };
    });
  } catch {
    return [];
  }
}

/**
 * Log a share event for the K-factor dashboard. Anonymous and signed-in users
 * both write here; the RLS `share_events_anyone_insert` policy permits any caller.
 * Columns match the migration-002+006 schema: `app_id` (the app SLUG, text since
 * migration 006), `shared_by` (the sharer's device/account id, text), `channel`
 * (the share kind, text since 006), `user_agent`, `referrer`. (Pre-006 this wrote
 * `owner_id`/`kind`/`ua`, which don't exist — every insert 400'd silently. H2 fix.)
 */
export type ShareKind =
  | 'web_share'
  | 'clipboard'
  | 'qr'
  | 'badge_click'
  | 'install'
  // §virality-P0 — per-channel share attribution from the post-gen card.
  | 'x'
  | 'whatsapp';

export async function logShareEvent(opts: {
  appId: string;
  ownerId: string;
  kind: ShareKind;
  ua?: string;
  referrer?: string;
}): Promise<void> {
  const c = cfg();
  if (!c.supabaseUrl || !c.supabaseAnonKey) return;
  try {
    await fetch(`${c.supabaseUrl}/rest/v1/share_events`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        app_id: opts.appId,
        shared_by: opts.ownerId,
        channel: opts.kind,
        user_agent: opts.ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 160) : null),
        referrer: opts.referrer ?? null
      }),
      keepalive: true
    });
  } catch {
    // Fire-and-forget.
  }
}
